
import { Fragment, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  ArrowDown,
  ArrowUp,
  BookOpen,
  Pencil,
  Plus,
  Reply,
  Trash2,
  UploadCloud,
  X,
} from 'lucide-react';
import type { MessageAttachment, TicketMessage } from '@/entities/ticket';
import { ticketsApi } from '@/shared/api/tickets';
import { resolveApiUrl } from '@/shared/lib/config';
import { cn } from '@/shared/lib/cn';
import { formatDayLabel } from '@/shared/lib/date';
import { useMediaQuery } from '@/shared/lib/useMediaQuery';
import { readDraft, writeDraft } from '@/shared/lib/chat-drafts';
import { copyToClipboard } from '@/shared/lib/clipboard';
import { formatQuote, useQuoteStore } from '@/features/wiki';
import { Avatar, Button, Dialog, DialogContent, DialogTitle, EmptyState, IconButton, MediaViewer, Scroller, Tooltip, UnreadBadge, toast, type MediaViewerItem } from '@/shared/ui';
import { MAX_MESSAGE_LENGTH, AttachmentIcon, ChatIcon, EmailIcon, NoteIcon, ReplyTemplateIcon } from '@/shared/config';
import type { TicketMailInfo } from '@/shared/api';
import { TemplatePanel, type TemplateContext } from './TemplatePanel';
import type { MessageKind, TemplateApplyMode } from '@/shared/api';
import { attachmentKind, isMediaKind } from '../lib/attachments';
import {
  attachmentKey,
  describeReplyTarget,
  groupMessages,
  isImage,
  isVideo,
  MAX_ATTACHMENTS,
  MESSAGE_MUTATION_WINDOW_MS,
  type DeleteRequest,
  type PendingFile,
  type ReplyTarget,
} from './chat/chat-lib';
import { useChatSocket, type TicketReader } from './chat/useChatSocket';
import { useStickToBottom } from './chat/useStickToBottom';
import { ChatMessage, type MailAction } from './chat/ChatMessage';
import { PendingStrip } from './chat/PendingStrip';
import { DeleteDialog } from './chat/DeleteDialog';
import { useRegisterChatBridge, type ChatBridge } from '../lib/chatBridge';
import { ComposerActionSheet, type ComposerAction } from './chat/ComposerActionSheet';
import sheetStyles from './chat/ComposerActionSheet.module.css';
import {
  mentionLabel,
  textToTokens,
  tokensToText,
  type MentionUser,
} from '@/shared/lib/mentions';
import styles from './TicketChat.module.css';

export interface TicketChatProps {
  ticketId: string;
  initialMessages: TicketMessage[];
  currentUserId: string;
  templateContext?: TemplateContext | null;
  canModifyMessages?: boolean;
  chatFrozen?: boolean;
  bare?: boolean;
  onMessagesChange?: (messages: TicketMessage[]) => void;
  mode?: MessageKind;
  active?: boolean;
  readAt?: string | null;
  onRead?: (lastReadAt: string) => void;
  onNoteCreated?: (message: TicketMessage) => void;
  mail?: ChatMailBridge;
}

export interface ChatMailBridge {
  info: TicketMailInfo | null;
  emailByDefault: boolean;
  canMove: boolean;
  confirmEmail: () => Promise<boolean>;
  sendAsEmail: (messageId: string) => Promise<void>;
  onMailAction: (action: MailAction, messageId: string) => void;
}

const EMPTY_READERS: TicketReader[] = [];

const DRAFTS = 'ticket';
const NOTE_DRAFTS = 'ticket-note';

const MENTION_QUERY_RE = /(?:^|\s)@([^\n@]{0,30})$/;

function detectMentionQuery(value: string, caret: number): { start: number; query: string } | null {
  const match = MENTION_QUERY_RE.exec(value.slice(0, caret));
  if (!match) return null;
  const query = match[1];
  if (query.split(' ').length > 3) return null;
  return { start: caret - query.length - 1, query };
}

export function TicketChat({
  ticketId,
  initialMessages,
  currentUserId,
  templateContext,
  canModifyMessages = false,
  chatFrozen = false,
  bare = false,
  onMessagesChange,
  mode = 'chat',
  active = true,
  readAt: notesReadAt = null,
  onRead,
  onNoteCreated,
  mail,
}: TicketChatProps) {
  const isNotes = mode === 'notes';
  const draftScope = isNotes ? NOTE_DRAFTS : DRAFTS;
  const [messages, setMessages] = useState<TicketMessage[]>(initialMessages);
  const [inputValue, setInputValue] = useState(() => readDraft(draftScope, ticketId));
  const [sending, setSending] = useState(false);
  const canEmail = Boolean(mail?.info?.canSendEmail);
  const [emailMode, setEmailMode] = useState(false);
  const emailDefault = Boolean(mail?.emailByDefault && canEmail);
  useEffect(() => {
    setEmailMode(emailDefault);
  }, [ticketId, emailDefault]);
  const [pending, setPending] = useState<PendingFile[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const closeTemplates = useCallback(() => setTemplatesOpen(false), []);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [replyTo, setReplyTo] = useState<ReplyTarget | null>(null);
  const wikiQuote = useQuoteStore((s) => s.quote);
  const quote = isNotes ? null : wikiQuote;
  const clearQuote = useQuoteStore((s) => s.clearQuote);
  const [mentionable, setMentionable] = useState<MentionUser[] | null>(null);
  const [mention, setMention] = useState<{ start: number; query: string } | null>(null);
  const mentionReqRef = useRef(0);
  const [mentionIndex, setMentionIndex] = useState(0);
  const pickedMentionsRef = useRef(new Map<string, string>());
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedAttachments, setSelectedAttachments] = useState<Set<string>>(new Set());
  const [deleteRequest, setDeleteRequest] = useState<DeleteRequest | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [flashId, setFlashId] = useState<string | null>(null);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftBeforeEditRef = useRef('');
  const inputValueRef = useRef(inputValue);
  inputValueRef.current = inputValue;
  const editingIdRef = useRef(editingId);
  editingIdRef.current = editingId;
  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const dragCounter = useRef(0);

  useEffect(() => {
    setMessages(initialMessages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId, initialMessages.length]);

  useEffect(() => {
    onMessagesChange?.(messages);
  }, [messages, onMessagesChange]);

  const { handleScroll, onImageLoad, atBottom, fresh, jumpToBottom } = useStickToBottom(messagesRef, ticketId, messages, currentUserId);

  const changeInput = useCallback(
    (value: string) => {
      setInputValue(value);
      if (!editingIdRef.current) writeDraft(draftScope, ticketId, value);
    },
    [ticketId, draftScope],
  );

  const handleRemoteDeleted = useCallback((messageId: string) => {
    setSelectedIds((prev) => {
      if (!prev.has(messageId)) return prev;
      const next = new Set(prev);
      next.delete(messageId);
      return next;
    });
    setSelectedAttachments((prev) => {
      const next = new Set([...prev].filter((k) => !k.startsWith(`${messageId}|`)));
      return next.size === prev.size ? prev : next;
    });
    setEditingId((prev) => (prev === messageId ? null : prev));
    setReplyTo((prev) => (prev?.messageId === messageId ? null : prev));
  }, []);

  const { typingUserId, emitTypingStart, emitTypingStop, reads, markRead } = useChatSocket({
    ticketId,
    currentUserId,
    setMessages,
    onMessageDeleted: handleRemoteDeleted,
    kind: mode,
    onNotesRead: onRead,
  });

  const otherReaders = useMemo(
    () => Object.values(reads).filter((r) => r.userId !== currentUserId),
    [reads, currentUserId],
  );
  const readersByMessageId = useMemo(() => {
    const map = new Map<string, TicketReader[]>();
    for (const m of messages) {
      if (m.authorId !== currentUserId) continue;
      const arr = otherReaders.filter((r) => r.lastReadAt >= m.createdAt);
      map.set(m.id, arr.length ? arr : EMPTY_READERS);
    }
    return map;
  }, [messages, otherReaders, currentUserId]);
  const readersOf = useCallback(
    (messageId: string) => readersByMessageId.get(messageId) ?? EMPTY_READERS,
    [readersByMessageId],
  );
  const [readersFor, setReadersFor] = useState<TicketReader[] | null>(null);

  const [unreadStartId, setUnreadStartId] = useState<string | null>(null);
  const unreadPlacedRef = useRef(false);
  const myReadAt = isNotes ? notesReadAt ?? undefined : reads[currentUserId]?.lastReadAt;
  useEffect(() => {
    if (unreadPlacedRef.current || !myReadAt) return;
    unreadPlacedRef.current = true;
    const first = messages.find((m) => m.authorId !== currentUserId && m.createdAt > myReadAt);
    setUnreadStartId(first?.id ?? null);
  }, [myReadAt, messages, currentUserId]);

  const lastMessageAt = messages.length ? messages[messages.length - 1].createdAt : null;
  useEffect(() => {
    if (!lastMessageAt || chatFrozen || !active) return;
    const read = () => {
      markRead(lastMessageAt);
      if (isNotes) onRead?.(lastMessageAt);
    };
    if (document.visibilityState === 'visible') {
      read();
      return;
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') read();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [lastMessageAt, chatFrozen, active, isNotes, markRead, onRead]);

  useEffect(
    () => () => {
      pending.forEach((p) => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    setInputValue(readDraft(draftScope, ticketId));
    setUnreadStartId(null);
    unreadPlacedRef.current = false;
    mentionReqRef.current++;
    setMentionable(null);
    setMention(null);
    pickedMentionsRef.current = new Map();
    setEditingId(null);
    setReplyTo(null);
    setSelectMode(false);
    setSelectedIds(new Set());
    setSelectedAttachments(new Set());
    setDeleteRequest(null);
    draftBeforeEditRef.current = '';
  }, [ticketId, draftScope]);


  const addFiles = useCallback((files: FileList | File[]) => {
    const MAX_FILE_MB = 20;
    const list = Array.from(files).filter((file) => {
      if (file.size > MAX_FILE_MB * 1024 * 1024) {
        toast.error(`«${file.name}» больше ${MAX_FILE_MB} МБ`);
        return false;
      }
      return true;
    });
    if (list.length === 0) return;
    setPending((prev) => {
      const room = MAX_ATTACHMENTS - prev.length;
      if (room <= 0) return prev;
      const next = list.slice(0, room).map<PendingFile>((file) => ({
        localId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        previewUrl: isImage(file.type) || isVideo(file.type) ? URL.createObjectURL(file) : null,
        progress: 0,
        status: 'ready',
      }));
      return [...prev, ...next];
    });
  }, []);

  const removePending = (localId: string) => {
    setPending((prev) => {
      const item = prev.find((p) => p.localId === localId);
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((p) => p.localId !== localId);
    });
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const files = e.clipboardData?.files;
    if (files && files.length > 0) {
      e.preventDefault();
      addFiles(files);
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    if (!e.dataTransfer.types.includes('Files')) return;
    dragCounter.current += 1;
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setDragOver(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current = 0;
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files);
  };


  const showActionError = useCallback((err: unknown) => {
    toast.error(
      err instanceof Error && err.message ? err.message : 'Не удалось выполнить действие',
    );
  }, []);

  const canMutate = useCallback(
    (m: TicketMessage) =>
      canModifyMessages &&
      m.authorId === currentUserId &&
      Date.now() - new Date(m.createdAt).getTime() < MESSAGE_MUTATION_WINDOW_MS,
    [canModifyMessages, currentUserId],
  );

  const copyText = useCallback((m: TicketMessage) => {
    const { text } = tokensToText(m.body, m.mentions);
    void copyToClipboard(text).then((ok) =>
      ok ? toast.success('Текст скопирован') : toast.error('Браузер не дал доступ к буферу обмена'),
    );
  }, []);


  const mentionOptions = useMemo(() => {
    if (!mention || !mentionable) return [];
    const q = mention.query.trim().toLowerCase();
    const list = q
      ? mentionable.filter((u) => u.displayName.toLowerCase().includes(q))
      : mentionable;
    return list.slice(0, 8);
  }, [mention, mentionable]);

  const refreshMentionable = useCallback(() => {
    const req = ++mentionReqRef.current;
    void ticketsApi
      .mentionable(ticketId, isNotes ? 'notes' : undefined)
      .then((res) => {
        if (req === mentionReqRef.current) setMentionable(res.data ?? []);
      })
      .catch(() => {
        if (req === mentionReqRef.current) setMentionable((prev) => prev ?? []);
      });
  }, [ticketId, isNotes]);

  const syncMention = useCallback(
    (value: string, caret: number) => {
      const found = detectMentionQuery(value, caret);
      setMention(found);
      setMentionIndex(0);
      if (found && !mention) refreshMentionable();
    },
    [mention, refreshMentionable],
  );

  const applyMention = useCallback(
    (user: MentionUser) => {
      const el = inputRef.current;
      if (!el || !mention) return;
      const caret = el.selectionStart ?? inputValueRef.current.length;
      const label = `${mentionLabel(user.displayName)} `;
      const next =
        inputValueRef.current.slice(0, mention.start) +
        label +
        inputValueRef.current.slice(caret);
      pickedMentionsRef.current.set(user.displayName, user.id);
      setInputValue(next);
      setMention(null);
      requestAnimationFrame(() => {
        const input = inputRef.current;
        if (!input) return;
        const pos = mention.start + label.length;
        input.focus();
        input.setSelectionRange(pos, pos);
      });
    },
    [mention],
  );


  const startEdit = useCallback((m: TicketMessage) => {
    if (editingIdRef.current === null) draftBeforeEditRef.current = inputValueRef.current;
    setEditingId(m.id);
    const { text, picked } = tokensToText(m.body, m.mentions);
    for (const [name, id] of picked) pickedMentionsRef.current.set(name, id);
    setInputValue(text);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setInputValue(draftBeforeEditRef.current);
    if (inputRef.current) inputRef.current.style.height = 'auto';
  }, []);

  const startReply = useCallback(
    (messageId: string, attachmentId?: string) => {
      if (editingIdRef.current) cancelEdit();
      setReplyTo({ messageId, ...(attachmentId ? { attachmentId } : {}) });
      inputRef.current?.focus();
    },
    [cancelEdit],
  );

  const scrollToQuoted = useCallback((messageId: string) => {
    const el = messagesRef.current?.querySelector(`[data-mid="${messageId}"]`);
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    setFlashId(messageId);
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => setFlashId(null), 1600);
  }, []);

  const chatBridge = useMemo<ChatBridge>(() => {
    const byId = new Map(messages.map((m) => [m.id, m]));
    return {
      has: (id) => byId.has(id),
      jumpTo: scrollToQuoted,
      reply: startReply,
      canDelete: (id) => {
        const m = byId.get(id);
        return Boolean(m && canMutate(m));
      },
      requestDeleteAttachment: (messageId, attachmentId) =>
        setDeleteRequest({ kind: 'attachment', messageId, attachmentId }),
    };
  }, [messages, scrollToQuoted, startReply, canMutate]);
  useRegisterChatBridge(ticketId, chatBridge);

  const enterSelectMode = useCallback((m: TicketMessage) => {
    setSelectMode(true);
    setSelectedIds(new Set([m.id]));
    setSelectedAttachments(new Set());
  }, []);

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
    setSelectedAttachments(new Set());
  };

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setSelectedAttachments((prev) => {
      const next = new Set([...prev].filter((k) => !k.startsWith(`${id}|`)));
      return next.size === prev.size ? prev : next;
    });
  }, []);

  const toggleSelectedAttachment = useCallback((messageId: string, attachmentId: string) => {
    if (selectedIdsRef.current.has(messageId)) return;
    setSelectedAttachments((prev) => {
      const key = attachmentKey(messageId, attachmentId);
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const selectedCount = selectedIds.size + selectedAttachments.size;

  const requestDeleteSelection = () => {
    setDeleteRequest({
      kind: 'bulk',
      messageIds: [...selectedIds],
      attachments: [...selectedAttachments].map((k) => {
        const [messageId, attachmentId] = k.split('|');
        return { messageId, attachmentId };
      }),
    });
  };

  const confirmDelete = async () => {
    if (!deleteRequest || deleting) return;
    setDeleting(true);
    try {
      if (deleteRequest.kind === 'bulk') {
        const res = await ticketsApi.deleteMessages(ticketId, {
          ...(deleteRequest.messageIds.length ? { messageIds: deleteRequest.messageIds } : {}),
          ...(deleteRequest.attachments.length ? { attachments: deleteRequest.attachments } : {}),
        });
        const data = (
          res as {
            data?: {
              deletedMessageIds: string[];
              updatedMessages: Array<{ id: string; attachments: MessageAttachment[] }>;
            };
          }
        ).data;
        const deletedIds = new Set(data?.deletedMessageIds ?? deleteRequest.messageIds);
        const updatedById = new Map((data?.updatedMessages ?? []).map((u) => [u.id, u.attachments]));
        setMessages((prev) =>
          prev
            .filter((m) => !deletedIds.has(m.id))
            .map((m) => (updatedById.has(m.id) ? { ...m, attachments: updatedById.get(m.id) } : m)),
        );
        if (editingId && deletedIds.has(editingId)) cancelEdit();
        exitSelectMode();
        const total = deletedIds.size + deleteRequest.attachments.length;
        toast.success(total > 1 ? `Удалено: ${total}` : 'Сообщение удалено');
      } else {
        const res = await ticketsApi.deleteMessageAttachment(
          ticketId,
          deleteRequest.messageId,
          deleteRequest.attachmentId,
        );
        const data = (res as { data?: { deletedMessage: boolean; attachments: MessageAttachment[] } })
          .data;
        if (data?.deletedMessage) {
          setMessages((prev) => prev.filter((m) => m.id !== deleteRequest.messageId));
        } else if (data) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === deleteRequest.messageId ? { ...m, attachments: data.attachments } : m,
            ),
          );
        }
        toast.success('Вложение удалено');
      }
      setDeleteRequest(null);
    } catch (err) {
      setDeleteRequest(null);
      showActionError(err);
    } finally {
      setDeleting(false);
    }
  };


  const phone = useMediaQuery('(max-width: 768px)');
  const composerActions: ComposerAction[] = [
    { key: 'file', label: 'Файл', Icon: AttachmentIcon, onSelect: () => fileInputRef.current?.click() },
    ...(templateContext && !isNotes
      ? [{ key: 'templates', label: 'Шаблон', Icon: ReplyTemplateIcon, onSelect: () => setTemplatesOpen(true) }]
      : []),
    ...(canEmail && !editingId
      ? [{
          key: 'email',
          label: 'Письмом',
          Icon: EmailIcon,
          pressed: emailMode,
          onSelect: () => setEmailMode((v) => !v),
        }]
      : []),
  ];

  const canSend =
    !sending &&
    !chatFrozen &&
    (editingId
      ? inputValue.trim().length > 0
      : inputValue.trim().length > 0 || pending.length > 0 || quote !== null);

  const handleSend = async () => {
    if (!canSend) return;
    const viaEmail = !editingId && emailMode && canEmail && Boolean(mail);
    if (viaEmail && !(await mail!.confirmEmail())) return;
    const text = textToTokens(inputValue.trim(), pickedMentionsRef.current);
    setSending(true);
    try {
      if (editingId) {
        const res = await ticketsApi.editMessage(ticketId, editingId, text);
        const updated = (res as { data?: TicketMessage }).data;
        if (updated) {
          setMessages((prev) => prev.map((m) => (m.id === updated.id ? { ...m, ...updated } : m)));
        }
        setEditingId(null);
        setInputValue(draftBeforeEditRef.current);
        if (inputRef.current) inputRef.current.style.height = 'auto';
        return;
      }

      let attachmentIds: string[] = [];
      if (pending.length > 0) {
        const batch = pending;
        setPending((prev) =>
          prev.map((p) => ({ ...p, status: 'uploading', progress: 0, error: undefined })),
        );
        const results = await Promise.allSettled(
          batch.map((p) =>
            ticketsApi.uploadAttachment(ticketId, p.file, (percent) =>
              setPending((prev) =>
                prev.map((x) => (x.localId === p.localId ? { ...x, progress: percent } : x)),
              ),
            ),
          ),
        );
        if (results.some((r) => r.status === 'rejected')) {
          const firstError = results.find((r) => r.status === 'rejected') as
            | PromiseRejectedResult
            | undefined;
          const errMsg =
            (firstError?.reason instanceof Error && firstError.reason.message) ||
            'Не удалось загрузить';
          setPending((prev) =>
            prev.map((p, i) =>
              results[i]?.status === 'rejected'
                ? { ...p, status: 'error', error: errMsg }
                : { ...p, status: 'ready', progress: 100 },
            ),
          );
          toast.error(errMsg);
          setSending(false);
          return;
        }
        attachmentIds = results.map(
          (r) => (r as PromiseFulfilledResult<MessageAttachment>).value.id,
        );
      }

      const body = quote ? `${formatQuote(quote)}${text}` : text;
      const res = await ticketsApi.addMessage(ticketId, body, attachmentIds, replyTo, isNotes);
      setInputValue('');
      writeDraft(draftScope, ticketId, '');
      setMention(null);
      pickedMentionsRef.current = new Map();
      setReplyTo(null);
      clearQuote();
      if (inputRef.current) inputRef.current.style.height = 'auto';
      pending.forEach((p) => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
      setPending([]);
      emitTypingStop();
      const newMsg = (res as { data?: TicketMessage }).data;
      if (newMsg) {
        setMessages((prev) => (prev.some((m) => m.id === newMsg.id) ? prev : [...prev, newMsg]));
        if (viaEmail) void mail!.sendAsEmail(newMsg.id);
      }
    } catch (err) {
      showActionError(err);
      setPending((prev) =>
        prev.map((p) =>
          p.status === 'uploading' ? { ...p, status: 'ready', progress: 100 } : p,
        ),
      );
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (mention && mentionOptions.length > 0) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex((i) => {
          const delta = e.key === 'ArrowDown' ? 1 : -1;
          return (i + delta + mentionOptions.length) % mentionOptions.length;
        });
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        applyMention(mentionOptions[Math.min(mentionIndex, mentionOptions.length - 1)]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMention(null);
        return;
      }
    }
    if (e.key === 'Escape' && (editingId || replyTo || quote)) {
      e.preventDefault();
      if (editingId) cancelEdit();
      else if (replyTo) setReplyTo(null);
      else clearQuote();
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
      return;
    }
    if (e.key === 'ArrowUp' && !mention && !editingId && !replyTo && !quote && inputValue === '' && pending.length === 0) {
      const last = [...messages].reverse().find((m) => canMutate(m));
      if (last) {
        e.preventDefault();
        startEdit(last);
      }
    }
  };

  const galleryItems = useMemo<MediaViewerItem[]>(
    () =>
      messages.flatMap((m) =>
        (m.attachments ?? [])
          .filter((a) => isMediaKind(attachmentKind(a.mimeType, a.fileName)))
          .map((a) => ({
            url: resolveApiUrl(a.url),
            fileName: a.fileName,
            type: attachmentKind(a.mimeType, a.fileName) === 'video' ? ('video' as const) : ('image' as const),
          })),
      ),
    [messages],
  );

  const openViewer = useCallback(
    (url: string) => {
      const idx = galleryItems.findIndex((g) => g.url === url);
      if (idx >= 0) setViewerIndex(idx);
    },
    [galleryItems],
  );

  const quoteInfoById = useMemo(() => {
    const map = new Map<string, ReturnType<typeof describeReplyTarget>>();
    for (const m of messages) {
      if (m.replyToMessageId) {
        map.set(
          m.id,
          describeReplyTarget(messages, currentUserId, {
            messageId: m.replyToMessageId,
            attachmentId: m.replyToAttachmentId,
          }),
        );
      }
    }
    return map;
  }, [messages, currentUserId]);

  const [pendingViewerIndex, setPendingViewerIndex] = useState<number | null>(null);
  const pendingMediaItems = useMemo<MediaViewerItem[]>(
    () =>
      pending
        .filter((p) => p.previewUrl)
        .map((p) => ({
          url: p.previewUrl!,
          fileName: p.file.name,
          type: isVideo(p.file.type) ? ('video' as const) : ('image' as const),
        })),
    [pending],
  );
  const openPendingViewer = (localId: string) => {
    const media = pending.filter((p) => p.previewUrl);
    const idx = media.findIndex((p) => p.localId === localId);
    if (idx >= 0) setPendingViewerIndex(idx);
  };

  const handleTemplateApplied = (result: {
    mode: TemplateApplyMode;
    renderedBody: string;
    messageId?: string;
  }) => {
    if (result.mode === 'email') toast.success('Письмо отправлено заявителю');
    if (!result.messageId) return;
    const message: TicketMessage = {
      id: result.messageId,
      ticketId,
      authorId: currentUserId,
      body: result.renderedBody,
      internal: result.mode === 'internal_note',
      createdAt: new Date().toISOString(),
    };
    if (message.internal) {
      onNoteCreated?.(message);
      toast.success('Заметка добавлена во вкладку «Заметки»');
      return;
    }
    setMessages((prev) => (prev.some((m) => m.id === message.id) ? prev : [...prev, message]));
  };

  const handleTemplateInsert = (text: string) => {
    setInputValue((prev) => (prev.trim() ? `${prev}\n${text}` : text));
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  return (
    <section
      className={cn(styles.chat, bare && styles.chatBare)}
      onDragEnter={handleDragEnter}
      onDragOver={(e) => e.preventDefault()}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {!bare && <h2 className={styles.title}>Чат заявки</h2>}

      <div className={styles.messagesWrap}>
      <Scroller
        className={styles.messages}
        viewportClassName={styles.messagesViewport}
        viewportRef={messagesRef}
        onScroll={handleScroll}
      >
        {messages.length === 0 &&
          (isNotes ? (
            <EmptyState
              className={styles.feedCenter}
              Icon={NoteIcon}
              title="Заметок пока нет"
              hint="Здесь инженеры пишут друг другу по заявке. Заявитель заметки не видит."
            />
          ) : bare ? (
            <EmptyState
              className={styles.feedCenter}
              Icon={ChatIcon}
              title="Сообщений пока нет"
              hint="Когда вы получите или напишите сообщение, оно отобразится здесь"
            />
          ) : (
            <div className={styles.empty}>Сообщений пока нет</div>
          ))}
        {groupMessages(messages, unreadStartId).map((g) => {
          const first = g.items[0];
          const own = first.authorId === currentUserId;
          return (
            <Fragment key={first.id}>
            {g.dayStart && <div className={styles.dateChip}>{formatDayLabel(first.createdAt)}</div>}
            {g.unreadStart && (
              <div
                className={styles.unreadDivider}
                role="separator"
                aria-label={isNotes ? 'Новые заметки' : 'Новые сообщения'}
              >
                <span>{isNotes ? 'Новые заметки' : 'Новые сообщения'}</span>
              </div>
            )}
            <div className={styles.group}>
              {!own && (
                <Avatar
                  className={styles.groupAvatar}
                  name={first.author?.displayName ?? 'Собеседник'}
                  seed={first.authorId}
                  src={first.author?.avatarUrl}
                />
              )}
              <div className={styles.groupStack}>
                {g.items.map((m, i) => (
                  <ChatMessage
                    key={m.id}
                    msg={m}
                    currentUserId={currentUserId}
                    showAuthor={!own && i === 0}
                    mutable={canMutate(m)}
                    selectMode={selectMode}
                    selected={selectedIds.has(m.id)}
                    selectedAttachments={selectedAttachments}
                    isEditing={editingId === m.id}
                    isFlashing={flashId === m.id}
                    readers={own && !isNotes ? readersOf(m.id) : EMPTY_READERS}
                    noteBadge={!isNotes}
                    onShowReaders={setReadersFor}
                    quoteInfo={quoteInfoById.get(m.id) ?? null}
                    onReply={startReply}
                    onEdit={startEdit}
                    onEnterSelect={enterSelectMode}
                    onToggleSelected={toggleSelected}
                    onToggleSelectedAttachment={toggleSelectedAttachment}
                    onRequestDelete={setDeleteRequest}
                    onCopyText={copyText}
                    onOpenViewer={openViewer}
                    onQuoteClick={scrollToQuoted}
                    onImageLoad={onImageLoad}
                    mailMeta={mail?.info?.messages[m.id]}
                    canEmail={canEmail}
                    canMove={mail?.canMove ?? false}
                    onMailAction={mail?.onMailAction}
                  />
                ))}
              </div>
            </div>
            </Fragment>
          );
        })}
      </Scroller>
      {!atBottom && messages.length > 0 && (
        <div className={styles.jump}>
          <IconButton label={fresh > 0 ? `К новым сообщениям (${fresh})` : 'К последним сообщениям'} onClick={jumpToBottom}>
            <ArrowDown size={18} aria-hidden />
          </IconButton>
          <UnreadBadge count={fresh} className={styles.jumpBadge} aria-hidden />
        </div>
      )}
      </div>

      {typingUserId && !chatFrozen && <div className={styles.typing}>Собеседник печатает...</div>}

      {chatFrozen ? (
        <div className={styles.frozenNotice} role="status">
          <ChatIcon size={16} aria-hidden />
          Заявка закрыта — чат неактивен
        </div>
      ) : (
      <>
      <PendingStrip pending={pending} onRemove={removePending} onPreview={openPendingViewer} />

      {selectMode && (
        <div className={styles.selectBar}>
          <span className={styles.selectCount}>
            Выбрано: {selectedCount}
            {selectedAttachments.size > 0 && ` (файлов: ${selectedAttachments.size})`}
          </span>
          <Button
            size="sm"
            variant="destructive"
            disabled={selectedCount === 0}
            onClick={requestDeleteSelection}
          >
            <Trash2 size={14} aria-hidden /> Удалить
          </Button>
          <Button size="sm" variant="ghost" onClick={exitSelectMode}>
            Отмена
          </Button>
        </div>
      )}

      {editingId && (
        <div className={styles.editBar}>
          <Pencil size={14} aria-hidden />
          <span className={styles.editBarLabel}>Редактирование сообщения</span>
          <Tooltip content="Отменить редактирование (Esc)">
            <button
              type="button"
              className={styles.editBarClose}
              onClick={cancelEdit}
              aria-label="Отменить редактирование"
            >
              <X size={14} aria-hidden />
            </button>
          </Tooltip>
        </div>
      )}

      {replyTo && !editingId && (() => {
        const info = describeReplyTarget(messages, currentUserId, replyTo);
        return (
          <div className={styles.editBar}>
            <Reply size={14} aria-hidden />
            {info.thumbUrl && <img className={styles.quoteThumb} src={info.thumbUrl} alt="" />}
            <span className={styles.editBarLabel}>
              Ответ: <strong>{info.author}</strong>
              {info.text && ` — ${info.text}`}
            </span>
            <Tooltip content="Отменить ответ (Esc)">
              <button
                type="button"
                className={styles.editBarClose}
                onClick={() => setReplyTo(null)}
                aria-label="Отменить ответ"
              >
                <X size={14} aria-hidden />
              </button>
            </Tooltip>
          </div>
        );
      })()}

      {quote && !editingId && (
        <div className={styles.quoteBar}>
          <BookOpen size={14} aria-hidden />
          <span className={styles.quoteBarBody}>
            <span className={styles.quoteBarTitle}>{quote.title}</span>
            <span className={styles.quoteBarText}>{quote.text}</span>
          </span>
          <Tooltip content="Убрать цитату (Esc)">
            <button
              type="button"
              className={styles.editBarClose}
              onClick={clearQuote}
              aria-label="Убрать цитату"
            >
              <X size={14} aria-hidden />
            </button>
          </Tooltip>
        </div>
      )}

      {emailMode && canEmail && !editingId && mail?.info?.requester?.address && (
        <div className={styles.emailTarget} aria-live="polite">
          <EmailIcon size={13} aria-hidden />
          <span className={styles.emailTargetAddress}>
            Уйдёт и письмом: {mail.info.requester.address}
            {mail.info.cc.length > 0 && ` · копия: ${mail.info.cc.map((c) => c.address).join(', ')}`}
          </span>
        </div>
      )}

      <div className={styles.inputRow}>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            if (e.target.files?.length) addFiles(e.target.files);
            e.target.value = '';
          }}
        />
        {composerActions.length > 1 && phone ? (
          <ComposerActionSheet
            actions={composerActions}
            triggerClassName={cn(styles.attachBtn, bare && styles.attachBtnRound)}
          />
        ) : (
        <>
        {templateContext && !isNotes && (
          <Tooltip content="Шаблоны ответов">
            <button
              type="button"
              className={cn(
                styles.attachBtn,
                bare && styles.attachBtnRound,
                templatesOpen && styles.attachBtnActive,
              )}
              onClick={() => setTemplatesOpen((o) => !o)}
              data-templates-toggle
              aria-label="Шаблоны ответов"
              aria-expanded={templatesOpen}
            >
              <ReplyTemplateIcon size={19} />
            </button>
          </Tooltip>
        )}
        {canEmail && !editingId && (
          <Tooltip
            content={
              emailMode
                ? 'Сообщение уйдёт и письмом — нажмите, чтобы отправить только в чат'
                : 'Отправить сообщение и письмом заявителю'
            }
          >
            <button
              type="button"
              className={cn(
                styles.attachBtn,
                bare && styles.attachBtnRound,
                emailMode && styles.emailToggleOn,
              )}
              onClick={() => setEmailMode((v) => !v)}
              aria-pressed={emailMode}
              aria-label="Отправить и письмом"
            >
              <EmailIcon size={bare ? 19 : 18} />
            </button>
          </Tooltip>
        )}
        <Tooltip content="Прикрепить файл">
          <button
            type="button"
            className={cn(styles.attachBtn, bare && styles.attachBtnRound)}
            onClick={() => fileInputRef.current?.click()}
            aria-label="Прикрепить файл"
          >
            <Plus size={bare ? 20 : 19} />
          </button>
        </Tooltip>
        </>
        )}
        {inputValue.length > MAX_MESSAGE_LENGTH * 0.8 && (
          <span className={styles.charCounter} aria-live="polite">
            {inputValue.length} / {MAX_MESSAGE_LENGTH}
          </span>
        )}
        {mention && (
          <Scroller
            className={styles.mentionPopup}
            viewportProps={{ role: 'listbox', 'aria-label': 'Упомянуть участника' }}
          >
            {mentionable === null || mentionOptions.length === 0 ? (
              <div className={styles.mentionEmpty}>
                {mentionable === null
                  ? 'Загрузка участников…'
                  : 'Никого не нашли среди участников заявки'}
              </div>
            ) : (
              <>
                {mentionOptions.map((u, i) => (
                  <button
                    key={u.id}
                    type="button"
                    role="option"
                    aria-selected={i === mentionIndex}
                    className={cn(
                      styles.mentionOption,
                      i === mentionIndex && styles.mentionOptionActive,
                    )}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      applyMention(u);
                    }}
                    onMouseEnter={() => setMentionIndex(i)}
                  >
                    <Avatar
                      className={styles.mentionAvatar}
                      name={u.displayName}
                      seed={u.id}
                      src={u.avatarUrl}
                    />
                    <span className={styles.mentionName}>{u.displayName}</span>
                  </button>
                ))}
                <div className={styles.mentionHint}>↑↓ — выбрать, Enter — подставить</div>
              </>
            )}
          </Scroller>
        )}
        { }
        <div className={styles['input-grow']} data-replicated-value={inputValue}>
        <textarea
          ref={inputRef}
          className={styles.input}
          value={inputValue}
          rows={1}
          maxLength={MAX_MESSAGE_LENGTH}
          onChange={(e) => {
            changeInput(e.target.value);
            syncMention(e.target.value, e.target.selectionStart ?? e.target.value.length);
            emitTypingStart();
          }}
          onClick={(e) =>
            syncMention(e.currentTarget.value, e.currentTarget.selectionStart ?? 0)
          }
          onBlur={() => {
            emitTypingStop();
            setTimeout(() => setMention(null), 120);
          }}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={
            isNotes
              ? 'Заметка для коллег — заявитель её не увидит'
              : bare
                ? 'Напишите сообщение'
                : 'Введите сообщение... (Shift+Enter — перенос строки)'
          }
          aria-label={isNotes ? 'Заметка по заявке' : 'Сообщение в чат заявки'}
          disabled={sending}
        />
        </div>
        <button
          type="button"
          className={cn(styles.sendBtn, bare && styles.sendBtnRound)}
          onClick={handleSend}
          disabled={!canSend}
          title={sending ? 'Отправка…' : 'Отправить (Enter)'}
        >
          <ArrowUp size={18} />
        </button>
      </div>
      </>
      )}

      <DeleteDialog
        request={deleteRequest}
        deleting={deleting}
        onConfirm={confirmDelete}
        onClose={() => setDeleteRequest(null)}
      />

      { }
      <Dialog open={readersFor != null} onOpenChange={(open) => !open && setReadersFor(null)}>
        <DialogContent className={styles.readersDialog}>
          <DialogTitle className={styles.readersTitle}>
            Прочитали{readersFor?.length ? ` · ${readersFor.length}` : ''}
          </DialogTitle>
          <ul className={styles.readersList}>
            {(readersFor ?? []).map((r) => (
              <li key={r.userId} className={styles.readerRow}>
                <Avatar
                  className={styles.readerAvatar}
                  name={r.displayName}
                  seed={r.userId}
                  src={r.avatarUrl}
                />
                <span className={styles.readerName}>{r.displayName}</span>
                <time className={styles.readerTime} dateTime={r.lastReadAt}>
                  {new Date(r.lastReadAt).toLocaleString('ru', {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </time>
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>

      {templateContext && !isNotes && phone ? (
        <Dialog open={templatesOpen} onOpenChange={(open) => !open && closeTemplates()}>
          <DialogContent flush className={sheetStyles.sheet} aria-label="Шаблоны ответов">
            <TemplatePanel
              variant="sheet"
              context={templateContext}
              onClose={closeTemplates}
              onInsert={handleTemplateInsert}
              onApplied={handleTemplateApplied}
            />
          </DialogContent>
        </Dialog>
      ) : (
        templatesOpen &&
        templateContext &&
        !isNotes && (
          <TemplatePanel
            context={templateContext}
            onClose={closeTemplates}
            onInsert={handleTemplateInsert}
            onApplied={handleTemplateApplied}
          />
        )
      )}

      {dragOver && (
        <div className={styles.dropOverlay}>
          <UploadCloud size={36} />
          Отпустите файлы, чтобы прикрепить
        </div>
      )}

      {viewerIndex !== null && galleryItems.length > 0 && (
        <MediaViewer
          items={galleryItems}
          index={Math.min(viewerIndex, galleryItems.length - 1)}
          onIndexChange={setViewerIndex}
          onClose={() => setViewerIndex(null)}
        />
      )}

      {pendingViewerIndex !== null && pendingMediaItems[pendingViewerIndex] && (
        <MediaViewer
          items={pendingMediaItems}
          index={pendingViewerIndex}
          onIndexChange={setPendingViewerIndex}
          onClose={() => setPendingViewerIndex(null)}
        />
      )}
    </section>
  );
}
